import {css, StyleSheet} from 'aphrodite';
import React, {useEffect, useMemo, useState} from 'react';

import * as AnalysisActions from 'analysis-tool/AnalysisActions';
import getDefaultMainViewItemForStep from 'analysis-tool/ui/getDefaultMainViewItemForStep';
import {AnalysisMainViewItemType, MainViewComponentProps} from 'analysis-tool/ui/main-view/MainViewTypes';
import StepConfigurationViewTemplate, {
  StepConfigurationViewTemplateProps,
} from 'analysis-tool/ui/view-templates/StepConfigurationViewTemplate';
import {assert} from 'Asserts';
import {DataFrameColumnDef} from 'data-frames/DataFrameTypes';
import CSVFilePreview from 'external-files/CSVFilePreview';
import {ExternalFlatFileDelimiter} from 'external-files/ExternalFileUtils';
import {PipelineStepType} from 'graphql-client/__generated/graphqlGlobalTypes';
import {InstrumentData} from 'instruments/instrument-data/InstrumentDataConstants';
import makeCancelable from 'lib/makeCancelable';
import makeUseGlobalClientState from 'ui-library/hooks/global-client-state/makeUseGlobalClientState';
import {AsyncStatus} from 'ui-library/hooks/useAsyncState';
import {CenteredHorizontalThrobber} from 'ui-library/HorizontalThrobber';
import * as net from 'util/Net';

import {ConnectionListingGraphQL} from '../../../../graphql-fragments/instruments/__generated/ConnectionListingGraphQL';
import ChooseDifferentDataFrameCreationMethodButton from '../ChooseDifferentDataFrameCreationMethodButton';
import {
  getTemplateDataFrameColumns,
  selectedColumnsToColumnMappings,
  setPreviewDataFrameColumnsFromFileString,
  unselectColumnsIfColumnIsNotInTemplateColumns,
} from '../column-mapping/ColumnMappingUtil';
import {DATA_FRAME_CREATION_NO_PREVIEW_PLACEHOLDER_TEXT} from '../DataFrameCreationConstants';
import {DataFrameNameState} from '../DataFrameCreationTypes';
import {
  getEnabledDatasetCreationMethods,
  getSharedDataFrameCreationMethodProps,
} from '../DataFrameCreationUtils';
import {DataFrameNameFieldProps} from '../steps/DataFrameNameField';
import ConnectionDataStepperForm from './ConnectionDataStepperForm';

const PREVIEW_DATA_FRAME_ROW_LIMIT = 1000;

export interface CreateDataFrameFromConnectionDataRequestData {
  dataFrameName: string;
}

const DEFAULT_DATA_FRAME_NAME_STATE: DataFrameNameState = {name: '', hasUserEdits: false};

const connectionIdGlobalState = makeUseGlobalClientState<string | null>(null);
const instrumentDataGlobalState = makeUseGlobalClientState<InstrumentData | null>(null);
const dataFrameNameGlobalState = makeUseGlobalClientState<DataFrameNameState>(DEFAULT_DATA_FRAME_NAME_STATE);

const isFinalizingGlobalState = makeUseGlobalClientState<boolean>(false);

function maybeRenderAdditionalPreviewContent(
  file: Pick<File, 'text'> | null,
  instrumentData: InstrumentData | null,
  asyncStatus: AsyncStatus
): (() => JSX.Element) | undefined {
  if (asyncStatus === AsyncStatus.LOADING) {
    return () => <CenteredHorizontalThrobber />;
  } else if (file == null || instrumentData == null) {
    return undefined;
  }
  return () => (
    <div className={css(styles.previewContainer)}>
      <CSVFilePreview
        file={file}
        url={instrumentData.downloadUrl}
        maxRows={PREVIEW_DATA_FRAME_ROW_LIMIT}
        // Only allow CSV files, because the backend cannot yet parse other delimiters
        delimiter={ExternalFlatFileDelimiter.COMMA}
      />
    </div>
  );
}

export default function CreateDataFrameFromConnectionDataView(
  props: MainViewComponentProps<AnalysisMainViewItemType.CONNECTION_DATA_DATASET_CONFIGURATION>
): JSX.Element {
  const [connectionId, setConnectionId] = connectionIdGlobalState.useGlobalClientState(props.clientStateId);
  const [instrumentData, setInstrumentData] = instrumentDataGlobalState.useGlobalClientState(
    props.clientStateId
  );
  const [dataFrameNameState, setDataFrameNameState] = dataFrameNameGlobalState.useGlobalClientState(
    props.clientStateId
  );
  const [isFinalizing, setIsFinalizing] = isFinalizingGlobalState.useGlobalClientState(props.clientStateId);
  const [file, setFile] = useState<Pick<File, 'text'> | null>(null);
  const [previewDataFrame, setPreviewDataFrame] = useState<{columns: Array<DataFrameColumnDef>} | null>(null);
  const templateDataFrameColumns = useMemo(
    () => getTemplateDataFrameColumns(props.analysis, props.selectedItem.stepIdToReplace),
    [props.analysis, props.selectedItem.stepIdToReplace]
  );
  const [selectedColumns, setSelectedColumns] = useState<Array<DataFrameColumnDef | null>>([]);
  const [isManuallyChanged, setIsManuallyChanged] = useState<boolean>(false);

  const [asyncStatus, setAsyncStatus] = useState<AsyncStatus>(AsyncStatus.NOT_FETCHED);

  if (previewDataFrame?.columns) {
    unselectColumnsIfColumnIsNotInTemplateColumns(
      selectedColumns,
      previewDataFrame.columns,
      setSelectedColumns
    );
  }

  useEffect(() => {
    if (instrumentData) {
      setAsyncStatus(AsyncStatus.LOADING);
      const filePromise = makeCancelable(Promise.resolve(net.get(instrumentData.downloadUrl)));
      filePromise.promise
        .then((newFileText: string) => {
          setPreviewDataFrameColumnsFromFileString(newFileText, setPreviewDataFrame);
          setFile({text: () => Promise.resolve(newFileText)});
          setAsyncStatus(AsyncStatus.SUCCESS);
        })
        .catch((response: Response) => {
          setAsyncStatus(AsyncStatus.ERROR);
        });
      return () => {
        filePromise.cancel();
        setAsyncStatus(AsyncStatus.NOT_FETCHED);
      };
    } else {
      setPreviewDataFrameColumnsFromFileString(null, setPreviewDataFrame);
      setFile(null);
      setAsyncStatus(AsyncStatus.NOT_FETCHED);
      return () => {};
    }
  }, [instrumentData]);

  const clearState = (): void => {
    setConnectionId(null);
    setInstrumentData(null);
    setDataFrameNameState(DEFAULT_DATA_FRAME_NAME_STATE);
  };

  async function onFinalize(): Promise<void> {
    assert(
      instrumentData != null && dataFrameNameState.name !== '',
      'Tried to finalize with incomplete form'
    );

    setIsFinalizing(true);

    try {
      const newStep = await AnalysisActions.addOrReplaceAndRunAnalysisStep(
        props.analysis.id,
        {
          stepType: PipelineStepType.CONNECTION_DATA_DATASET,
          stepVersion: 0,
          stepConfig: {
            dataFrameName: dataFrameNameState.name,
            connectionId: instrumentData.connectionId,
            connectionFileId: instrumentData.id,
          },
        },
        props.selectedItem.stepIdToReplace
          ? {
              originalStepId: props.selectedItem.stepIdToReplace,
              columnMapping: selectedColumnsToColumnMappings(selectedColumns, templateDataFrameColumns),
              allDisplayNames: previewDataFrame!.columns.map((c) => c.displayName),
            }
          : undefined
      );
      props.onCloseStepInitializationView(getDefaultMainViewItemForStep(newStep));
      clearState();
    } finally {
      setIsFinalizing(false);
    }
  }

  const onCancel: StepConfigurationViewTemplateProps['onCancel'] = (...args) => {
    props.onCloseStepInitializationView(...args);
    clearState();
  };

  const dataFrameNameFieldProps: DataFrameNameFieldProps = {
    ...dataFrameNameState,
    onNameChange: (newName: string) => {
      setDataFrameNameState({name: newName, hasUserEdits: true});
    },
    disabled: false,
  };
  return (
    <StepConfigurationViewTemplate
      {...getSharedDataFrameCreationMethodProps(props.selectedItem)}
      title="Connection data"
      clientStateId={props.clientStateId}
      dataFrames={[]}
      disableFinalizeButton={
        dataFrameNameState.name.trim() === '' ||
        instrumentData == null ||
        (props.selectedItem.stepIdToReplace != null && previewDataFrame == null)
      }
      isFinalizing={isFinalizing}
      isLoadingPreview={asyncStatus === AsyncStatus.LOADING}
      messages={[]}
      noPreviewPlaceholderOptions={{
        title: DATA_FRAME_CREATION_NO_PREVIEW_PLACEHOLDER_TEXT,
        subtitle: 'Select a connection and file to see a preview of your table.',
      }}
      onCancel={onCancel}
      onFinalize={onFinalize}
      renderAdditionalPreviewContent={maybeRenderAdditionalPreviewContent(file, instrumentData, asyncStatus)}
      toolbar={
        getEnabledDatasetCreationMethods().length > 1 && (
          <ChooseDifferentDataFrameCreationMethodButton
            onOpenStepInitializationView={(newSelectedItem: {
              type: AnalysisMainViewItemType.CHOOSE_DATASET_CREATION_METHOD;
            }) => {
              props.onOpenStepInitializationView({
                ...newSelectedItem,
                stepIdToReplace: props.selectedItem.stepIdToReplace,
              });
            }}
          />
        )
      }
    >
      <ConnectionDataStepperForm
        dataFrameNameFieldProps={dataFrameNameFieldProps}
        connectionId={connectionId}
        onChangeConnection={(connection: ConnectionListingGraphQL) => {
          setConnectionId(connection.id);
          if (!dataFrameNameState.hasUserEdits) {
            setDataFrameNameState({name: `${connection.name} data`, hasUserEdits: false});
          }
        }}
        columnMappingProps={
          props.selectedItem.stepIdToReplace != null
            ? {
                templateDataFrameColumns,
                newDataFrameColumns: previewDataFrame?.columns ?? null,
                newDataFrameState: asyncStatus,
                selectedColumns,
                setSelectedColumns,
                ignoreColumnValidationErrors: true,
                isManuallyChanged,
                setIsManuallyChanged,
              }
            : undefined
        }
        instrumentData={instrumentData}
        onChangeInstrumentData={setInstrumentData}
      />
    </StepConfigurationViewTemplate>
  );
}

const styles = StyleSheet.create({
  previewContainer: {
    overflowX: 'scroll',
  },
});
